import { PayloadAction, createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit';
import { okaoAxios } from '../../helpers/axios';
import { RootState, store } from '../../redux/store';
import { RestaurantApprovalListItemDTO, ApprovalMode, ApprovalModeResponse, ApproveRestaurantRequest, RejectRestaurantRequest } from './types';
import { AlertColor } from '@mui/material/Alert';

export interface IRestaurantApprovalSlice {
  status: string;
  restaurants: RestaurantApprovalListItemDTO[];
  count: number;
  pageSize: number;
  currentPage: number;
  currentStatus: 'PENDING' | 'AUTO_APPROVED' | 'MANUAL_REVIEW_REQUIRED' | 'REJECTED';
  approvalMode: ApprovalMode | null;
  shouldOpen: boolean;
  message: string | undefined;
  severity: AlertColor | undefined;
}

const initialState: IRestaurantApprovalSlice = {
  status: 'idle',
  restaurants: [],
  count: 0,
  pageSize: 25,
  currentPage: 0,
  currentStatus: 'PENDING',
  approvalMode: null,
  shouldOpen: false,
  message: undefined,
  severity: undefined
};

// Fetch approval mode
export const fetchApprovalMode = createAsyncThunk<ApprovalModeResponse, undefined>(
  'api/fetchApprovalMode',
  async () => {
    const auth = store.getState().secure.accessToken ?? '';
    const response = await okaoAxios(auth).get<ApprovalModeResponse>(
      `${process.env.REACT_APP_DIS_URL ?? window.location.origin}/admin/settings/restaurant-approval-mode`
    );
    return response.data;
  }
);

// Update approval mode
export const updateApprovalMode = createAsyncThunk<ApprovalModeResponse, ApprovalMode>(
  'api/updateApprovalMode',
  async (mode: ApprovalMode) => {
    const auth = store.getState().secure.accessToken ?? '';
    const response = await okaoAxios(auth).put<ApprovalModeResponse>(
      `${process.env.REACT_APP_DIS_URL ?? window.location.origin}/admin/settings/restaurant-approval-mode`,
      { mode }
    );
    return response.data;
  }
);

// Fetch restaurant approvals list
export const fetchRestaurantApprovals = createAsyncThunk(
  'api/fetchRestaurantApprovals',
  async (params: { page: number; size: number; status?: string }) => {
    const auth = store.getState().secure.accessToken ?? '';
    const statusParam = params.status !== 'ALL' ? `&status=${params.status}` : '';
    const response = await okaoAxios(auth).get<{
      result: RestaurantApprovalListItemDTO[];
      total: number;
    }>(
      `${process.env.REACT_APP_DIS_URL ?? window.location.origin}/admin/restaurants/approvals?page=${params.page}&size=${params.size}${statusParam}`
    );
    return response.data;
  }
);

// Approve restaurant
export const approveRestaurant = createAsyncThunk<undefined, ApproveRestaurantRequest>(
  'api/approveRestaurant',
  async (request: ApproveRestaurantRequest): Promise<undefined> => {
    const auth = store.getState().secure.accessToken ?? '';
    await okaoAxios(auth).post(
      `${process.env.REACT_APP_DIS_URL ?? window.location.origin}/admin/restaurants/${request.restaurantId}/approve`,
      { adminAccessLevel: request.adminAccessLevel }
    );
    return undefined;
  }
);

// Reject restaurant
export const rejectRestaurant = createAsyncThunk<undefined, RejectRestaurantRequest>(
  'api/rejectRestaurant',
  async (request: RejectRestaurantRequest): Promise<undefined> => {
    const auth = store.getState().secure.accessToken ?? '';
    await okaoAxios(auth).post(
      `${process.env.REACT_APP_DIS_URL ?? window.location.origin}/admin/restaurants/${request.restaurantId}/reject`,
      { reason: request.reason }
    );
    return undefined;
  }
);

export const restaurantApprovalSlice = createSlice({
  name: 'restaurantApproval',
  initialState,
  reducers: {
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
    },
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setCurrentStatus: (state, action: PayloadAction<'PENDING' | 'AUTO_APPROVED' | 'MANUAL_REVIEW_REQUIRED' | 'REJECTED'>) => {
      state.currentStatus = action.payload;
      state.currentPage = 0; // Reset to first page when status changes
    },
    setOpen: (state, action: PayloadAction<boolean>) => {
      state.shouldOpen = action.payload;
    }
  },
  extraReducers: builder => {
    builder
      .addCase(fetchApprovalMode.fulfilled, (state, action) => {
        state.approvalMode = action.payload.mode;
        state.status = 'idle';
      })
      .addCase(updateApprovalMode.fulfilled, (state, action) => {
        state.approvalMode = action.payload.mode;
        state.status = 'idle';
        state.shouldOpen = true;
        state.message = 'Approval mode updated successfully';
        state.severity = 'success';
      })
      .addCase(fetchRestaurantApprovals.fulfilled, (state, action) => {
        state.restaurants = action.payload.result;
        state.count = action.payload.total;
        state.status = 'idle';
      })
      .addMatcher(
        isAnyOf(approveRestaurant.fulfilled, rejectRestaurant.fulfilled),
        (state) => {
          state.status = 'idle';
          state.shouldOpen = true;
          state.message = 'Restaurant approval action completed successfully';
          state.severity = 'success';
        }
      )
      .addMatcher(
        isAnyOf(
          fetchApprovalMode.pending,
          updateApprovalMode.pending,
          fetchRestaurantApprovals.pending,
          approveRestaurant.pending,
          rejectRestaurant.pending
        ),
        (state) => {
          state.status = 'loading';
        }
      )
      .addMatcher(
        isAnyOf(
          fetchApprovalMode.rejected,
          updateApprovalMode.rejected,
          fetchRestaurantApprovals.rejected,
          approveRestaurant.rejected,
          rejectRestaurant.rejected
        ),
        (state, action) => {
          state.shouldOpen = true;
          state.status = 'error';
          state.severity = 'error';
          state.message = 'Error occurred while performing restaurant approval action';
          console.error(action.error);
        }
      );
  }
});

export const selectRestaurantApprovals = (state: RootState): RestaurantApprovalListItemDTO[] =>
  state.restaurantApproval.restaurants ?? [];
export const selectRowCount = (state: RootState): number => state.restaurantApproval.count;
export const selectPageSize = (state: RootState): number => state.restaurantApproval.pageSize;
export const selectCurrentPage = (state: RootState): number => state.restaurantApproval.currentPage;
export const selectCurrentStatus = (state: RootState): 'PENDING' | 'AUTO_APPROVED' | 'MANUAL_REVIEW_REQUIRED' | 'REJECTED' =>
  state.restaurantApproval.currentStatus;
export const selectApprovalMode = (state: RootState): ApprovalMode | null =>
  state.restaurantApproval.approvalMode;
export const selectSeverity = (state: RootState): AlertColor | undefined =>
  state.restaurantApproval.severity;
export const selectMessage = (state: RootState): string | undefined =>
  state.restaurantApproval.message;
export const selectShouldOpen = (state: RootState): boolean => state.restaurantApproval.shouldOpen;
export const selectIsLoading = (state: RootState): boolean => state.restaurantApproval.status === 'loading';

export const { setPageSize, setCurrentPage, setCurrentStatus, setOpen } = restaurantApprovalSlice.actions;
export default restaurantApprovalSlice.reducer;
